import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import {
  getCallWindowPrefs,
  readLocalFlag,
  writeLocalFlag,
  type CallWindowPrefs,
} from "../../lib/callWindowPrefs";
import { Dialer } from "./Dialer";
import { CallGlass } from "./glass/CallGlass";
import { IncomingCall } from "./incoming/IncomingCall";
import { CallPill, LiveCall } from "./LiveCall";
import { PilotPanel } from "./pilot/PilotPanel";
import { useLivePilot } from "./pilot/useLivePilot";
import { useCallOutcome } from "./queue/useCallOutcome";
import { useRingback } from "./outgoing/useRingback";
import { useCallContext } from "./CallProvider";
import { isLiveCall } from "./callState";
import { useIsNarrow } from "../shell/navDrawer";
import { HeldStrip, KnockCard } from "./waiting";

/** One duration for the whole morph, so the box and both surfaces stay in step. */
const MORPH = { type: "spring" as const, stiffness: 420, damping: 40, mass: 0.9 };

/**
 * The AI panel's own sweep: a tween, not the spring the box runs on.
 *
 * Deliberately a different curve from `MORPH`. The box is a solid being resized
 * and a spring is how a solid moves; the panel is content being uncovered, and a
 * spring on it overshoots - the transcript would arrive, slide a few pixels past
 * its column and settle back, which reads as a glitch rather than as a gesture.
 * Slightly longer than the box, so the surface has somewhere to arrive into
 * before the words get there.
 */
const SWEEP = { type: "tween" as const, duration: 0.42, ease: [0.22, 1, 0.36, 1] as const };

/**
 * The three widths this one surface takes. The pad and the call were already two
 * of them, and that is the point: opening the AI panel is a third value on a
 * morph that has carried width changes since the window was built, not a new
 * piece of animation. `.crm-modal` sets 520px in CSS and all three override it
 * inline, so the stylesheet never enters into it.
 *
 * Fullscreen is not on this list. It is not a fourth width - it is the window
 * giving up on being a card at all, which is why it also drops the corners.
 */
const WIDTH = { dialer: "min(920px, 100%)", call: "min(470px, 100%)", pilot: "min(880px, 100%)" };

/**
 * The operator's own choices, per browser. Absent means "never asked" and the
 * account default in Settings decides - see `lib/callWindowPrefs.ts`.
 */
const PILOT_OPEN_KEY = "crm.call.pilot.open";
const FULLSCREEN_KEY = "crm.call.fullscreen";

/**
 * The call window: the dial pad, the call itself, and the corner pill.
 *
 * The modal and the pill share one `layoutId`, so tucking a call away is one
 * box moving and shrinking rather than one window closing and another opening.
 * That is the whole point of the gesture - the call has to read as the same
 * object in both places, or the pill looks like a notification about a call
 * instead of the call.
 *
 * Escape and the backdrop never drop a running call: while one is up they tuck
 * it into the corner. Only the red button ends it.
 */
export function CallWindow() {
  const ctx = useCallContext();
  const t = useT();
  const [minimized, setMinimized] = useState(false);
  /**
   * The glass pane itself, for the refraction filter that is drawn to its size.
   * A ref rather than a query, because there is a moment during the morph when
   * the window and the corner pill are both mounted and a selector would find
   * whichever one the DOM happened to hold first.
   */
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  /**
   * A suggestion on its way into the call note. It lives up here because the two
   * ends of the trip are siblings: the panel raises it, `CallNoteBox` inside the
   * call column receives it. A signal rather than a second writer - that box owns
   * the text and its own debounced save, and two things patching `CallLog.note`
   * means one of them loses without saying so.
   */
  const [noteAppend, setNoteAppend] = useState<{ text: string; at: number } | null>(null);
  const narrow = useIsNarrow();
  /**
   * What this browser has been told, and what the account says otherwise.
   *
   * Two states rather than one, because "closed" and "never chosen" are
   * different answers: an operator who has pressed the button owns the panel
   * from then on, and one who never has follows whatever Settings holds - which
   * is the whole point of having a default there at all.
   */
  const [pilotChoice, setPilotChoice] = useState<boolean | null>(() => readLocalFlag(PILOT_OPEN_KEY));
  const [fullChoice, setFullChoice] = useState<boolean | null>(() => readLocalFlag(FULLSCREEN_KEY));
  const [defaults, setDefaults] = useState<CallWindowPrefs | null>(null);

  // Fetched once, at start-up, not per call: the window has to know its shape
  // the instant the phone rings, and a request in flight while a call window
  // opens is a window that opens small and then jumps.
  useEffect(() => {
    let live = true;
    void getCallWindowPrefs().then((prefs) => {
      if (live) setDefaults(prefs);
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * The AI panel is a desk feature and it stays one.
   *
   * Stacked under the call on a phone (see index.css, the 900px rule) it puts
   * about 360px of suggestions between the keypad and the red button, so
   * hanging up means scrolling first - on the one screen where the control
   * everybody reaches for has to be exactly where it was a second ago. The
   * PREFERENCE is untouched: turning the phone sideways, or opening the same
   * account on a laptop, brings the panel straight back.
   */
  /**
   * A live AI call this window is taking over (ring R6d). The co-pilot stays out
   * of it for now: its tap and its transcript are built around a call this
   * operator placed or answered, and the AI's own call already has both.
   */
  const takeover = ctx?.state.takeover ?? false;
  const pilotOpen = (pilotChoice ?? defaults?.pilotOpen ?? false) && !narrow && !takeover;
  const phase = ctx?.state.phase ?? "idle";
  const live = isLiveCall(phase);
  const ringing = phase === "incoming";
  const dialer = ctx?.dialerOpen ?? false;
  /**
   * Fullscreen, and only ever for a call.
   *
   * The dial pad has one column of buttons in it and a ringing card has one
   * question on it; neither is improved by being blown up to the size of a
   * monitor. What fullscreen is FOR is the AI panel - reading the transcript,
   * the checklist and the next steps without scrolling any of them - so it
   * applies exactly where that panel lives.
   */
  const fullscreen = (fullChoice ?? defaults?.fullscreen ?? false) && !narrow;
  const full = fullscreen && (phase === "ended" || live) && !ringing;

  /**
   * The operator's own ring while a call they placed rings at the other end.
   *
   * Mounted HERE, not on the live call surface: tucking a ringing call into the
   * corner pill unmounts that surface, and a ring that stops when the window is
   * minimised is a ring nobody can trust. This component is on screen for the
   * whole session, which is exactly what a ring needs.
   *
   * `viaMobile` is excluded and is not a detail. On those calls the phase
   * reaches "ringing" while the OPERATOR'S OWN phone is the thing ringing, in
   * their pocket, with no browser audio in the call at all - so a ringback here
   * would play over a handset they are already lifting.
   */
  // Nor on a takeover, where the person at the far end has been talking to the
  // AI all along: there is no phone ringing anywhere to stand in for.
  useRingback(
    ctx?.state.direction === "outbound" && phase === "ringing" && !ctx.state.viaMobile && !takeover,
  );

  const togglePilot = useCallback(() => {
    setPilotChoice((open) => {
      const next = !(open ?? defaults?.pilotOpen ?? false);
      writeLocalFlag(PILOT_OPEN_KEY, next);
      return next;
    });
  }, [defaults]);

  const toggleFullscreen = useCallback(() => {
    setFullChoice((open) => {
      const next = !(open ?? defaults?.fullscreen ?? false);
      writeLocalFlag(FULLSCREEN_KEY, next);
      return next;
    });
  }, [defaults]);

  /**
   * The label this call gets when it ends, held here rather than in the ended
   * screen. This component is mounted for the whole session, so the registry is
   * fetched once instead of once per call and the chips are drawn the instant
   * the call is over - which on a run of two hundred is the difference between
   * a wrap-up and a wait.
   */
  const outcome = useCallOutcome(ctx?.state.callLogId ?? null);

  /**
   * The tap runs only while the panel is open, and that is deliberate: closing
   * the panel is the operator saying they do not want this call listened to, so
   * it has to stop the spending as well as the drawing. Window numbering picks
   * up from what the server already holds, so reopening mid-call continues the
   * transcript instead of restarting it.
   */
  const pilot = useLivePilot({
    callLogId: ctx?.state.callLogId ?? null,
    active: phase === "active",
    enabled: pilotOpen && phase !== "idle",
    paused: ctx?.state.hold ?? false,
    getStreams: ctx?.getCallStreams ?? (() => null),
  });

  const pill = live && minimized;

  /**
   * A flag on the body while the pill is up.
   *
   * The pill is fixed to the bottom-right corner at `z-index: 86` and, on a
   * narrow screen, the full width of it - which puts it exactly over the mail
   * desk's Compose button and the call desk's Dial button. Those two belong to
   * a screen and cannot see a portal rendered next to `<body>`, so the fact
   * that a call is tucked away is published here and the buttons move
   * themselves out of the way (index.css, `body[data-call-pill]`). Declared up
   * here with the other effects, above the `if (!ctx)` return: a hook below an
   * early return is a hook that sometimes does not run.
   */
  useEffect(() => {
    if (!pill) return;
    document.body.dataset.callPill = "1";
    return () => {
      delete document.body.dataset.callPill;
    };
  }, [pill]);

  // A finished call cannot stay tucked away: there is nothing left to come back
  // to, and a pill counting nothing is worse than no pill.
  useEffect(() => {
    if (!live) setMinimized(false);
  }, [live]);

  useEffect(() => {
    if (!ctx) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // A ringing call is left alone on purpose. Escape is a reflex, and the
      // two things it could plausibly mean here - decline, or get this box off
      // my screen - are not the same thing at all.
      if (ringing) return;
      if (live) setMinimized(true);
      else if (dialer) ctx.closeDialer();
      else if (phase === "ended" || phase === "error") ctx.dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctx, live, ringing, dialer, phase]);

  if (!ctx) return null;

  const showCall = phase !== "idle";
  const open = (showCall || dialer) && !minimized;
  const dismiss = () => {
    // Same reasoning as Escape above: clicking beside a ringing phone is not an
    // answer to it.
    if (ringing) return;
    if (live) setMinimized(true);
    else if (showCall) ctx.dismiss();
    else ctx.closeDialer();
  };

  return createPortal(
    <>
      {/* Outside the presence on purpose: this draws nothing and occupies no
          space - it is a `<defs>` holding the displacement map the pane's
          `backdrop-filter` names. Inside `AnimatePresence` it would be torn out
          at the start of the exit animation and the window would spend its last
          200ms with a backdrop-filter pointing at a filter that no longer
          exists, which draws no blur at all. */}
      <CallGlass target={surfaceRef} radius={full ? 0 : 32} active={open} />

      <AnimatePresence>
        {open ? (
          <motion.div
            key="scrim"
            className={cn("crm-scrim", full && "is-full")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) dismiss();
            }}
          >
            {/* `is-glass` is the call window's own surface: a translucent pane
                that refracts the desk behind it, the same material the site
                chrome's Liquid Glass header is made of. Only this window has it,
                and deliberately so - every other dialog in the CRM is a form the
                operator reads and fills in, where a background that moves under
                the text is noise. A call is a thing that HAPPENS, on top of work
                still going on underneath, and the glass is what says so. */}
            <motion.div
              ref={surfaceRef}
              layoutId="crm-call-surface"
              // No height for the dial pad, on purpose. It used to carry an
              // `is-pad` class worth 700px on the two-column layout, and every
              // pixel by which that overshot the keypad landed in one gap under
              // the number. The pad sizes itself now (`.crm-modal` and
              // `DialPicks.tsx`), so there is nothing to say here beyond the
              // width - which is the one dimension that really does differ
              // between dialling, ringing and talking.
              className={cn("crm-modal is-glass flex flex-col overflow-hidden p-0", full && "is-full")}
              style={{
                // A ringing phone keeps the narrow width whatever the AI panel
                // is set to: the card has one question on it, and there is no
                // call yet for a panel to say anything about.
                width: full
                  ? "100%"
                  : ringing
                    ? WIDTH.call
                    : showCall
                      ? pilotOpen
                        ? WIDTH.pilot
                        : WIDTH.call
                      : WIDTH.dialer,
                // Filling the screen means filling it. A rounded card floating
                // edge to edge reads as a window that failed to maximise, and the
                // corners would be cutting the transcript at that size anyway.
                height: full ? "100%" : undefined,
                borderRadius: full ? 0 : 32,
              }}
              transition={MORPH}
              role="dialog"
              aria-modal="true"
              aria-label={t("calls.dial.title")}
            >
              {ringing && ctx.state.incoming ? (
                <IncomingCall
                  incoming={ctx.state.incoming}
                  onAccept={ctx.acceptCall}
                  onReject={ctx.rejectCall}
                />
              ) : showCall ? (
                <>
                {/* The call on hold sits above whatever the call in front is
                    doing, across both columns: it is about neither of them. */}
                {ctx.waiting.held ? (
                  <HeldStrip
                    held={ctx.waiting.held}
                    canTakeBack={ctx.waiting.canTakeBack}
                    onTakeBack={ctx.waiting.takeBack}
                  />
                ) : null}
                {/* Two columns on one surface. The row is here rather than
                    inside either column, so neither of them has to know how
                    wide the other one is.
                    `flex-1` only when the modal has a height of its own to
                    fill. On the card the row IS the height, and a zero
                    flex-basis there asks an auto-height parent to size itself
                    from nothing. */}
                <div className={cn("crm-cp-row flex min-h-0", full && "flex-1")}>
                  <LiveCall
                    state={ctx.state}
                    queue={ctx.queue}
                    outcome={outcome}
                    noteAppend={noteAppend}
                    onAddToNote={(text) => setNoteAppend({ text, at: Date.now() })}
                    pilotOpen={pilotOpen}
                    fullscreen={fullscreen}
                    canFullscreen={!narrow}
                    onTogglePilot={togglePilot}
                    onToggleFullscreen={toggleFullscreen}
                    getStreams={ctx.getCallStreams}
                    getAudio={ctx.getCallAudio}
                    onHangup={ctx.hangup}
                    onMute={ctx.toggleMute}
                    onHold={ctx.toggleHold}
                    onDigit={ctx.sendDigit}
                    onTransfer={ctx.startTransfer}
                    onCancelTransfer={ctx.stopTransfer}
                    onStopRecording={ctx.stopRecording}
                    onMinimize={() => setMinimized(true)}
                    onDismiss={ctx.dismiss}
                    onRedial={() => ctx.state.target && ctx.startCall(ctx.state.target)}
                    onNext={ctx.nextInQueue}
                    // Stop and close are one gesture, and the button says both:
                    // ending the run while leaving the finished call's window
                    // up would be a window with nothing left to do in it.
                    onStopQueue={() => {
                      ctx.stopQueue();
                      ctx.dismiss();
                    }}
                  />
                  {/* The panel arrives by sliding OUT of the call column to the
                      right, and leaves by sliding back into it. Two elements to
                      do it: the slot holds the space and clips, the sweep inside
                      carries the surface across it. One element cannot, because a
                      translated flex child is still painted over its neighbour -
                      the panel would wipe across the keypad on its way in.

                      The fade on the slot is deliberately a FIFTH of the travel.
                      It is here because `AnimatePresence` needs an animation on
                      the element it tracks to hold the exit open long enough for
                      the sweep below to finish - not because the panel is meant
                      to fade in. Run at the full duration it fought the gesture:
                      the surface spent most of its journey at low opacity, so it
                      read as materialising in place rather than as sliding out
                      from behind the call.

                      `initial={false}` on the presence: the very first render of
                      a call window must not play the arrival, or every call with
                      the panel switched on would start with a sweep nobody
                      asked for. */}
                  <AnimatePresence initial={false}>
                    {pilotOpen ? (
                      <motion.div
                        key="pilot"
                        className="crm-cp-slot"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ...SWEEP, opacity: { duration: 0.09, ease: "linear" } }}
                      >
                        <motion.div
                          className="crm-cp-sweep"
                          initial={{ x: "-100%" }}
                          animate={{ x: "0%" }}
                          exit={{ x: "-100%" }}
                          transition={SWEEP}
                        >
                          <PilotPanel pilot={pilot} onAddToNote={(text) => setNoteAppend({ text, at: Date.now() })} />
                        </motion.div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="crm-m-x absolute right-5 top-5 z-[2]"
                    onClick={ctx.closeDialer}
                    aria-label={t("common.close")}
                  >
                    <X className="size-4" />
                  </button>
                  <Dialer
                    prefill={ctx.dialerPrefill}
                    canSearchRegister={ctx.canSearchRegister}
                    onCall={(req) =>
                      ctx.startCall({
                        phone: req.number,
                        companyName: req.companyName,
                        regCode: req.regCode,
                        // The site the brain looked at, so the pre-call briefing
                        // reads it by primary key rather than guessing the
                        // newest run under this register code.
                        leadDomain: req.domain,
                        source: "manual",
                        fromNumber: req.fromNumber,
                        record: req.flags?.record,
                        transcribe: req.flags?.transcribe,
                        notice: req.flags?.announce,
                        // `?? undefined`: the flags carry `null` for "the
                        // account's own wording", and the request field is
                        // absent for the same thing. Sending an explicit null
                        // would be a third spelling of one answer.
                        noticeLocale: req.flags?.announceLocale ?? undefined,
                        // Where the operator is sitting, from the same footer
                        // strip as the three flags above it.
                        viaMobile: req.flags?.viaMobile,
                      })
                    }
                  />
                </>
              )}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* A second call during this one. Outside the window on purpose: it has to
          be seen while the call is tucked into the corner pill, too. */}
      {ctx.waiting.knock ? (
        <KnockCard
          knock={ctx.waiting.knock}
          block={ctx.waiting.answerBlock}
          holdsFront={live}
          onAnswer={ctx.waiting.answer}
          onDecline={ctx.waiting.decline}
        />
      ) : null}

      {/* `is-glass` on the pill as well as on the window, because it is the same
          object: the two share a `layoutId`, so a pill made of something else
          would have the surface change substance halfway through the morph. */}
      <AnimatePresence>
        {pill ? (
          <motion.div
            layoutId="crm-call-surface"
            className="crm-callpill is-glass"
            style={{ borderRadius: 999 }}
            transition={MORPH}
            role="dialog"
            aria-label={t("calls.dial.title")}
          >
            <CallPill
              state={ctx.state}
              onOpen={() => setMinimized(false)}
              onMute={ctx.toggleMute}
              onHold={ctx.toggleHold}
              onHangup={ctx.hangup}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>,
    document.body,
  );
}
